import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, List, X, Info, Warning, Lightbulb } from '@phosphor-icons/react';
import { useSEO } from '@/hooks/useSEO';
import PublicNav from '@/components/layout/PublicNav';
import PublicFooter from '@/components/layout/PublicFooter';
import DocsSidebar from '@/components/layout/DocsSidebar';
import DocsTOC from '@/components/layout/DocsTOC';
import { getArticle } from './docsData';

function ContentBlock({ block }) {
  switch (block.type) {
    case 'paragraph':
      return <p className="text-[13px] text-muted-foreground leading-relaxed mb-4">{block.text}</p>;
    case 'heading':
      if (block.level === 2) return <h2 id={block.id} className="font-display text-lg font-bold text-foreground mt-8 mb-3 scroll-mt-24">{block.text}</h2>;
      return <h3 id={block.id} className="font-display text-base font-bold text-foreground mt-6 mb-2 scroll-mt-24">{block.text}</h3>;
    case 'list':
      return (
        <ul className="space-y-1.5 mb-4 ml-4">
          {block.items.map((item, i) => (
            <li key={i} className="text-[13px] text-muted-foreground leading-relaxed list-disc">{item}</li>
          ))}
        </ul>
      );
    case 'steps':
      return (
        <ol className="space-y-3 mb-4">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-3 text-[13px] text-muted-foreground leading-relaxed">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-secondary flex items-center justify-center text-[10px] font-bold text-foreground mt-0.5">{i + 1}</span>
              <span>{item}</span>
            </li>
          ))}
        </ol>
      );
    case 'tip':
      return (
        <div className="flex gap-3 p-3 rounded-sm border border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30 mb-4" data-testid="callout-tip">
          <Lightbulb size={16} weight="bold" className="flex-shrink-0 text-blue-600 dark:text-blue-400 mt-0.5" />
          <p className="text-[12px] text-blue-900 dark:text-blue-200 leading-relaxed">{block.text}</p>
        </div>
      );
    case 'warning':
      return (
        <div className="flex gap-3 p-3 rounded-sm border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30 mb-4" data-testid="callout-warning">
          <Warning size={16} weight="bold" className="flex-shrink-0 text-amber-600 dark:text-amber-400 mt-0.5" />
          <p className="text-[12px] text-amber-900 dark:text-amber-200 leading-relaxed">{block.text}</p>
        </div>
      );
    case 'info':
      return (
        <div className="flex gap-3 p-3 rounded-sm border border-border bg-secondary/30 mb-4" data-testid="callout-info">
          <Info size={16} weight="bold" className="flex-shrink-0 text-muted-foreground mt-0.5" />
          <p className="text-[12px] text-muted-foreground leading-relaxed">{block.text}</p>
        </div>
      );
    default:
      return null;
  }
}

export default function DocsArticle() {
  const { category, slug } = useParams();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const article = getArticle(category, slug);

  useSEO(article ? {
    title: `${article.title} — Admitrix Docs`,
    description: article.content?.[0]?.text || '',
  } : {
    title: 'Docs — Admitrix',
    description: 'Admitrix documentation and tutorials',
  });

  // Breadcrumb structured data
  useEffect(() => {
    if (!article) return;
    const schema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://admitrix.in/" },
        { "@type": "ListItem", "position": 2, "name": "Docs", "item": "https://admitrix.in/docs" },
        { "@type": "ListItem", "position": 3, "name": article.categoryTitle, "item": `https://admitrix.in/docs/${article.categoryId}/${article.categoryId === 'getting-started' ? 'welcome' : 'overview'}` },
        { "@type": "ListItem", "position": 4, "name": article.title, "item": `https://admitrix.in/docs/${article.categoryId}/${article.slug}` },
      ]
    };
    let script = document.getElementById('breadcrumb-schema');
    if (!script) {
      script = document.createElement('script');
      script.id = 'breadcrumb-schema';
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);
    return () => { if (script.parentNode) script.parentNode.removeChild(script); };
  }, [article]);

  // Scroll to top on article change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [category, slug]);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="font-display text-2xl font-bold text-foreground mb-2">Article not found</h1>
          <Link to="/docs" className="text-sm underline" style={{ color: '#E04B2A' }}>Back to docs</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background text-foreground min-h-screen">
      <PublicNav />

      <div className="pt-16 max-w-[1400px] mx-auto">
        {/* Mobile sidebar toggle */}
        <div className="lg:hidden sticky top-16 z-30 bg-background border-b border-border px-4 py-2">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="flex items-center gap-2 text-[12px] font-semibold text-muted-foreground hover:text-foreground"
            data-testid="docs-sidebar-toggle"
          >
            {sidebarOpen ? <X size={16} /> : <List size={16} />}
            {article.categoryTitle} / {article.title}
          </button>
        </div>

        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div className="lg:hidden fixed inset-0 z-20 bg-background/80 backdrop-blur-sm top-16" onClick={() => setSidebarOpen(false)}>
            <div className="w-72 h-full bg-background border-r border-border p-4 overflow-y-auto" onClick={e => e.stopPropagation()}>
              <DocsSidebar onNavigate={() => setSidebarOpen(false)} />
            </div>
          </div>
        )}

        <div className="flex">
          {/* Desktop sidebar */}
          <aside className="hidden lg:block w-60 flex-shrink-0 border-r border-border h-[calc(100vh-4rem)] sticky top-16 overflow-y-auto p-4">
            <DocsSidebar />
          </aside>

          {/* Main content */}
          <main className="flex-1 min-w-0 px-6 lg:px-12 py-8 max-w-3xl">
            {/* Breadcrumb */}
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mb-6" data-testid="docs-breadcrumb">
              <Link to="/docs" className="hover:text-foreground transition-colors">Docs</Link>
              <span>/</span>
              <Link to={`/docs/${article.categoryId}/${article.categoryId === 'getting-started' ? 'welcome' : 'overview'}`} className="hover:text-foreground transition-colors">{article.categoryTitle}</Link>
              <span>/</span>
              <span className="text-foreground font-medium">{article.title}</span>
            </div>

            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-[10px] font-mono uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm" style={{ color: article.categoryColor, backgroundColor: `${article.categoryColor}12` }}>
                  {article.type === 'overview' ? 'Overview' : 'Guide'}
                </span>
                <span className="text-[11px] text-muted-foreground">{article.readTime} read</span>
              </div>
              <h1 className="font-display text-3xl font-extrabold text-foreground tracking-tight">{article.title}</h1>
            </div>

            {/* Article body */}
            <div data-testid="docs-article-body">
              {article.content.map((block, i) => (
                <ContentBlock key={i} block={block} />
              ))}
            </div>

            {/* Prev / Next */}
            <div className="flex items-center justify-between mt-12 pt-6 border-t border-border">
              {article.prev ? (
                <Link to={`/docs/${article.prev.categoryId}/${article.prev.slug}`} className="flex items-center gap-2 text-[13px] text-muted-foreground hover:text-foreground transition-colors group" data-testid="docs-prev">
                  <ArrowLeft size={14} weight="bold" className="group-hover:-translate-x-0.5 transition-transform" />
                  {article.prev.title}
                </Link>
              ) : <div />}
              {article.next ? (
                <Link to={`/docs/${article.next.categoryId}/${article.next.slug}`} className="flex items-center gap-2 text-[13px] text-muted-foreground hover:text-foreground transition-colors group" data-testid="docs-next">
                  {article.next.title}
                  <ArrowRight size={14} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
                </Link>
              ) : <div />}
            </div>
          </main>

          {/* Desktop TOC */}
          <aside className="hidden xl:block w-44 flex-shrink-0 h-[calc(100vh-4rem)] sticky top-16 overflow-y-auto py-8 pr-4">
            <DocsTOC content={article.content} />
          </aside>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
}
